import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { useOneDriveGraphStorageQuota } from '@office-start/graph-service';
import { useIdentity } from '@office-start/identity-service';
import { launchUrl } from '@office-start/launcher-service';
import { useLocStrings } from '@office-start/loc-service';
import { useIsSubscribedUser } from '@office-start/subscription-info-service';
import { logImpression, logUserAction } from '@office-start/telemetry-service';
import { appendQuery } from '@office-start/url-parser-utility';
import { StorageAlertLevel, } from '@office-start/welcome-badge-types';
import { useCallback, useEffect, useMemo } from 'react';
import { LoginHintParam, ManageStorageOneDriveUrl, Storage80PercentMultiplier, Storage90PercentMultiplier, storageDisabled, } from '../models/constants';
import { getStateNameProps } from '../utilities/getStateNameProps';
import { getStorageSizeString } from '../utilities/getStorageSizeString';
import { getUsedStorageBarWidth } from '../utilities/getUsedStorageBarWidth';
import { useDimeCallback } from './useDimeCallback';
export function useStorageQuota(_a) {
    var _b, _c;
    var area = _a.area, showEnforcement = _a.showEnforcement, _d = _a.logFullQuota, logFullQuota = _d === void 0 ? false : _d;
    var identity = useIdentity().data;
    var context = useOfficeComponentContext().data;
    var locStrings = useLocStrings().data;
    var featureGates = useFeatureGates().data;
    // $TODO: Reafactor
    // Decouple experiences from storage and pull common logic out.
    var welcomeBadgeResources = locStrings === null || locStrings === void 0 ? void 0 : locStrings.welcomeBadgeResources;
    var myContentUsqResources = locStrings === null || locStrings === void 0 ? void 0 : locStrings.edgeworthResources;
    var useUsq = useMemo(function () {
        var _a, _b;
        return ((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.useUSQGates) === null || _b === void 0 ? void 0 : _b.useUSQEnabled) || false;
    }, [featureGates]);
    var welcomeBadgeV2Enabled = useMemo(function () {
        var _a, _b;
        return (((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.welcomeBadgeV2Gates) === null || _b === void 0 ? void 0 : _b.welcomeBadgeV2Enabled) ||
            false);
    }, [featureGates]);
    var showDime = useDimeCallback({
        baseUrl: ManageStorageOneDriveUrl,
        area: area,
    });
    // Allow dime to be shown when 'Get more storage' is clicked...
    // even if the user is subscribed.
    var showDimeGetMoreStorage = useDimeCallback({
        area: area,
        baseUrl: ManageStorageOneDriveUrl,
        disableSubscriptionCheck: true,
    });
    var wbShowStoragePercentageEnabled = useMemo(function () {
        var _a, _b;
        return (((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.wbShowStoragePercentageGates) === null || _b === void 0 ? void 0 : _b.wbShowStoragePercentageEnabled) || false);
    }, [featureGates]);
    var myContentUsqEnabled = useMemo(function () {
        var _a, _b;
        return (((_b = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _a === void 0 ? void 0 : _a.myContentUsqGates) === null || _b === void 0 ? void 0 : _b.myContentUsqEnabled) ||
            false);
    }, [featureGates]);
    var oneDriveGraphStorageItem = (_c = (_b = useOneDriveGraphStorageQuota(useUsq)) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.result;
    var used = useMemo(function () { return (oneDriveGraphStorageItem === null || oneDriveGraphStorageItem === void 0 ? void 0 : oneDriveGraphStorageItem.used) || 0; }, [oneDriveGraphStorageItem === null || oneDriveGraphStorageItem === void 0 ? void 0 : oneDriveGraphStorageItem.used]);
    var total = useMemo(function () { return (oneDriveGraphStorageItem === null || oneDriveGraphStorageItem === void 0 ? void 0 : oneDriveGraphStorageItem.total) || 0; }, [oneDriveGraphStorageItem === null || oneDriveGraphStorageItem === void 0 ? void 0 : oneDriveGraphStorageItem.total]);
    var state = useMemo(function () { return (oneDriveGraphStorageItem === null || oneDriveGraphStorageItem === void 0 ? void 0 : oneDriveGraphStorageItem.state) || 'empty'; }, [oneDriveGraphStorageItem === null || oneDriveGraphStorageItem === void 0 ? void 0 : oneDriveGraphStorageItem.state]);
    var storageStateNameProps = getStateNameProps(state, myContentUsqResources);
    // In this scenario, new users are people who haven't started using storage
    var isNewUser = useMemo(function () { return oneDriveGraphStorageItem && oneDriveGraphStorageItem.used <= 0; }, [oneDriveGraphStorageItem]);
    // Only show upsell to unsubscribed users
    var isSubscribedUser = useIsSubscribedUser();
    var isDimeEnabledForUser = useMemo(function () {
        return (!!showDime &&
            !isSubscribedUser &&
            used >= total * Storage80PercentMultiplier &&
            !(identity === null || identity === void 0 ? void 0 : identity.isPipl) // Fallback to opening oneDrive if Pipl
        );
    }, [identity === null || identity === void 0 ? void 0 : identity.isPipl, isSubscribedUser, showDime, total, used]);
    var isMyContentPage = area === 'MyContent';
    var canRenderOnPage = useMemo(function () {
        if (isMyContentPage) {
            return myContentUsqEnabled;
        }
        else if (area === 'WelcomeBadge') {
            return welcomeBadgeV2Enabled && !!welcomeBadgeResources;
        }
        return false;
    }, [
        area,
        isMyContentPage,
        myContentUsqEnabled,
        welcomeBadgeResources,
        welcomeBadgeV2Enabled,
    ]);
    var canRender = useMemo(function () {
        return !!(context &&
            oneDriveGraphStorageItem &&
            total > 0 &&
            canRenderOnPage);
    }, [canRenderOnPage, context, oneDriveGraphStorageItem, total]);
    var oneDriveUrl = useMemo(function () {
        var url = ManageStorageOneDriveUrl;
        if (context) {
            url = context.loginHint
                ? appendQuery(ManageStorageOneDriveUrl, "".concat(LoginHintParam, "=").concat(context.loginHint))
                : ManageStorageOneDriveUrl;
        }
        return url;
    }, [context]);
    var storageSizeResourcesMap = useMemo(function () {
        return new Map([
            [
                'fileSizeBytesSingular',
                welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageSingularByte,
            ],
            ['fileSizeBytesPlural', welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageBytes],
            ['fileSizeKB', welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageKB],
            ['fileSizeMB', welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageMB],
            ['fileSizeGB', welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageGB],
            ['fileSizeTB', welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageTB],
        ]);
    }, [
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageBytes,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageGB,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageKB,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageMB,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageSingularByte,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageTB,
    ]);
    var storageUsedOrAvailableLabel = useMemo(function () {
        return isNewUser
            ? welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.firstTimeAvailableStorage // 100%
            : getStorageSizeString(storageSizeResourcesMap, +used, {
                trimDecimal: true,
            });
    }, // Eg. 56 MB
    [
        isNewUser,
        storageSizeResourcesMap,
        used,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.firstTimeAvailableStorage,
    ]);
    var totalStorageQuotaLabel = useMemo(function () {
        return getStorageSizeString(storageSizeResourcesMap, +total, {
            trimDecimal: true,
        });
    }, [storageSizeResourcesMap, total]); // gives localized string Eg 1TB
    var usedStorageBarWidth = useMemo(function () {
        return getUsedStorageBarWidth(used, total);
    }, [total, used]);
    useEffect(function () {
        if (canRender) {
            logImpression({
                eventName: "WelcomeBadgeV2_Impression" /* KnownImpressionInstrumentationEvents.WelcomeBadgeV2Impression */,
                area: area,
                subArea: 'StorageQuota',
                distribution: isDimeEnabledForUser
                    ? 'StorageQuotaDimeExperience'
                    : 'StorageQuotaDefaultExperience',
            });
        }
    }, [area, canRender, isDimeEnabledForUser]);
    var logStorageQuotaDime = useCallback(function () {
        logUserAction({
            id: 'StorageQuota_Dime',
            area: area,
            subArea: 'StorageQuota',
            result: 'Navigate',
            target: 'Other',
            eventName: 'StorageQuota_Dime',
        });
    }, [area]);
    // Storage Click handler
    var onStorageClick = useCallback(function () {
        if (isDimeEnabledForUser && !isMyContentPage) {
            logStorageQuotaDime();
            if (showDime) {
                showDime();
            }
        }
        else {
            var linkTarget = '_blank';
            var action = 'OpenFileLocation';
            logUserAction({
                id: 'StorageQuota_OneDrive',
                area: area,
                subArea: 'StorageQuota',
                result: 'Navigate',
                target: 'Other',
                eventName: 'StorageQuota_OneDrive',
            });
            launchUrl(oneDriveUrl, {
                action: action,
                linkTarget: linkTarget,
            });
        }
    }, [
        area,
        isDimeEnabledForUser,
        isMyContentPage,
        logStorageQuotaDime,
        oneDriveUrl,
        showDime,
    ]);
    var storageAlertLevel = useMemo(function () {
        if (isNewUser) {
            return StorageAlertLevel.None;
        }
        else if (used > total) {
            return StorageAlertLevel.Over;
        }
        else if (used === total) {
            return StorageAlertLevel.Full;
        }
        else if (!useUsq && used > total * Storage80PercentMultiplier) {
            // Normal warning state w/out USQ
            return StorageAlertLevel.Warning;
        }
        else if (useUsq &&
            used >= total * Storage80PercentMultiplier &&
            used < total * Storage90PercentMultiplier) {
            // New Warning state w/ USQ
            return StorageAlertLevel.WarningUSQ;
        }
        else if (useUsq && used >= total * Storage90PercentMultiplier) {
            // New Critical state w/ USQ
            return StorageAlertLevel.CriticalUSQ;
        }
        else {
            return StorageAlertLevel.Default;
        }
    }, [isNewUser, total, useUsq, used]);
    var canLogFullQuota = storageAlertLevel === StorageAlertLevel.Full ||
        storageAlertLevel === StorageAlertLevel.Over;
    useEffect(function () {
        if (canRender && logFullQuota && canLogFullQuota) {
            logImpression({
                eventName: "User_Storage_Full_Or_Over_Impression" /* KnownImpressionInstrumentationEvents.UserStorageFullOrOverImpression */,
                area: area,
            });
        }
    }, [area, canLogFullQuota, canRender, logFullQuota]);
    var storageLabel = useMemo(function () {
        return isNewUser
            ? welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.firstTimeStorageMessage
            : welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageMessage;
    }, [
        isNewUser,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.firstTimeStorageMessage,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageMessage,
    ]);
    var getSubscribedStorageToolTip = useCallback(function (storageAlertLevel) {
        if (storageAlertLevel === StorageAlertLevel.Over) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.paidUserStorageOverTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.Full) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageFullWarningTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.Warning) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.paidUserStorageWarningTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.None) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
        else {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
    }, [
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.paidUserStorageOverTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.paidUserStorageWarningTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageFullWarningTooltip,
    ]);
    var getUSQSubscribedStorageToolTip = useCallback(function (storageAlertLevel) {
        if (storageAlertLevel === StorageAlertLevel.Over) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageOverTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.Full) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageFullTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.WarningUSQ) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageWarningTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.CriticalUSQ) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageCriticalTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.None) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
        else {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
    }, [
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageCriticalTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageFullTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageOverTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageWarningTooltip,
    ]);
    var getFreeStorageToolTip = useCallback(function (storageAlertLevel) {
        if (isDimeEnabledForUser && StorageAlertLevel.Over) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.freeUserStorageOverTooltipWithDime;
        }
        else if (isDimeEnabledForUser && StorageAlertLevel.Full) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageFullWarningTooltipWithDime;
        }
        else if (storageAlertLevel === StorageAlertLevel.Over) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.freeUserStorageOverTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.Full) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageFullWarningTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.Warning) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.freeUserStorageWarningTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.None) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
        else {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
    }, [
        isDimeEnabledForUser,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.freeUserStorageOverTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.freeUserStorageOverTooltipWithDime,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.freeUserStorageWarningTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageFullWarningTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.storageFullWarningTooltipWithDime,
    ]);
    var getUSQFreeStorageToolTip = useCallback(function (storageAlertLevel) {
        if (storageAlertLevel === StorageAlertLevel.Over) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageOverTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.Full) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageFullTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.WarningUSQ) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageWarningTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.CriticalUSQ) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageCriticalTooltip;
        }
        else if (storageAlertLevel === StorageAlertLevel.None) {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
        else {
            return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip;
        }
    }, [
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.defaultStorageTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageCriticalTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageFullTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageOverTooltip,
        welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqUserStorageWarningTooltip,
    ]);
    var storageToolTip = useMemo(function () {
        if (isSubscribedUser) {
            if (useUsq) {
                return getUSQSubscribedStorageToolTip(storageAlertLevel);
            }
            else {
                return getSubscribedStorageToolTip(storageAlertLevel);
            }
        }
        else {
            if (useUsq) {
                return getUSQFreeStorageToolTip(storageAlertLevel);
            }
            else {
                return getFreeStorageToolTip(storageAlertLevel);
            }
        }
    }, [
        getFreeStorageToolTip,
        getSubscribedStorageToolTip,
        getUSQFreeStorageToolTip,
        getUSQSubscribedStorageToolTip,
        isSubscribedUser,
        storageAlertLevel,
        useUsq,
    ]);
    var enforcementToolTip = useMemo(function () {
        return welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqPreEnforcementTooltip;
    }, [welcomeBadgeResources === null || welcomeBadgeResources === void 0 ? void 0 : welcomeBadgeResources.usqPreEnforcementTooltip]);
    // TODO: Reduce redundancy between this check and the one in StorageQuota control
    // Temp soln to log impression for pre-enforcement icon
    var canRenderPreEnforcementIcon = showEnforcement &&
        (storageAlertLevel == StorageAlertLevel.Warning ||
            storageAlertLevel == StorageAlertLevel.WarningUSQ ||
            storageAlertLevel == StorageAlertLevel.CriticalUSQ ||
            storageAlertLevel == StorageAlertLevel.Full ||
            storageAlertLevel == StorageAlertLevel.Over);
    useEffect(function () {
        if (canRenderPreEnforcementIcon && enforcementToolTip) {
            logImpression({
                eventName: "USQ_Pre_Enforcement_Tooltip_Impression" /* KnownImpressionInstrumentationEvents.USQPreEnforcementTooltipImpression */,
                area: 'WelcomeBadge',
                subArea: 'StorageQuota',
            });
        }
    }, [enforcementToolTip, canRenderPreEnforcementIcon]);
    var storagePercentage = useMemo(function () { return (used / total) * 100 || 0; }, [used, total]);
    var getStoragePercentageInfo = useCallback(function () {
        //Storage hasn't been used or percentage flight isn't enabled
        if (!storagePercentage || !wbShowStoragePercentageEnabled) {
            return '';
        }
        // Storage has been used < 1%
        else if (storagePercentage < 1) {
            return ' (0%)';
        }
        // Storage has been used >= 1%
        else {
            return " (".concat(Math.floor(storagePercentage), "%)");
        }
    }, [storagePercentage, wbShowStoragePercentageEnabled]);
    // Used for MyContent 'Get more storage' click
    var onGetMoreStorageClick = useCallback(function () {
        if (showDimeGetMoreStorage) {
            logStorageQuotaDime();
            showDimeGetMoreStorage();
        }
    }, [logStorageQuotaDime, showDimeGetMoreStorage]);
    return useMemo(function () {
        if (canRender) {
            return {
                enforcementToolTip: enforcementToolTip !== null && enforcementToolTip !== void 0 ? enforcementToolTip : '',
                storageAlertLevel: storageAlertLevel,
                storageDisabled: storageDisabled,
                storageLabel: storageLabel !== null && storageLabel !== void 0 ? storageLabel : '',
                storageStateNameProps: storageStateNameProps,
                storageToolTip: storageToolTip !== null && storageToolTip !== void 0 ? storageToolTip : '',
                storageUsedOrAvailableLabel: storageUsedOrAvailableLabel !== null && storageUsedOrAvailableLabel !== void 0 ? storageUsedOrAvailableLabel : '',
                totalStorageQuotaLabel: totalStorageQuotaLabel !== null && totalStorageQuotaLabel !== void 0 ? totalStorageQuotaLabel : '',
                usedStorageBarWidth: usedStorageBarWidth,
                onStorageClick: onStorageClick,
                storagePercentage: function () { return getStoragePercentageInfo(); },
                onGetMoreStorageClick: onGetMoreStorageClick,
            };
        }
        else {
            return undefined;
        }
    }, [
        canRender,
        enforcementToolTip,
        storageAlertLevel,
        storageLabel,
        storageStateNameProps,
        storageToolTip,
        storageUsedOrAvailableLabel,
        totalStorageQuotaLabel,
        getStoragePercentageInfo,
        usedStorageBarWidth,
        onStorageClick,
        onGetMoreStorageClick,
    ]);
}
//# sourceMappingURL=useStorageQuota.js.map